import React, { useState, useEffect } from 'react'
import { getStatements, getAgreedStatements, addAgreedStatement, removeAgreedStatement } from '../../lib/spektrix'

const CommunicationPreferences = () => {
  const [statements, setStatements] = useState([])
  const [agreedStatements, setAgreedStatements] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [savingIds, setSavingIds] = useState(new Set())
  const [message, setMessage] = useState(null)

  useEffect(() => {
    loadStatements()
  }, [])

  const loadStatements = async () => {
    setIsLoading(true)
    try {
      // Fetch both statements and agreed statements in parallel
      const [statementsData, agreedData] = await Promise.all([
        getStatements(),
        getAgreedStatements()
      ])
      
      console.log('Statements:', statementsData)
      console.log('Agreed statements:', agreedData)
      
      // Create a map of agreed statements for easy lookup
      const agreedMap = new Map()
      agreedData.forEach(agreed => {
        agreedMap.set(agreed.id, agreed)
      })
      
      // Combine statements with their agreed status
      const combinedStatements = statementsData.map(statement => ({
        ...statement,
        opted: agreedMap.has(statement.id),
        agreedStatement: agreedMap.get(statement.id)
      }))
      
      setStatements(combinedStatements)
      setAgreedStatements(agreedData)
    } catch (error) {
      console.error('Failed to load statements:', error)
      setMessage({ type: 'error', text: 'Failed to load communication preferences. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggle = async (statement) => {
    // Add to saving set
    setSavingIds(prev => new Set(prev).add(statement.id))
    setMessage(null)
    
    try {
      // Toggle the opted state
      const newOptedState = !statement.opted
      
      if (newOptedState) {
        // Opt in - POST to add agreed statement
        await addAgreedStatement(statement.id)
      } else {
        // Opt out - DELETE the agreed statement
        await removeAgreedStatement(statement.id)
      }
      
      // Update the local state immediately without reloading
      setStatements(prev => 
        prev.map(s => 
          s.id === statement.id 
            ? { ...s, opted: newOptedState }
            : s
        )
      )
      
      // Don't show success message to prevent layout shifts
      setMessage(null)
    } catch (error) {
      console.error('Failed to update preference:', error)
      setMessage({ 
        type: 'error', 
        text: error.message || 'Failed to update preference. Please try again.' 
      })
      // Revert the state on error
      setStatements(prev => 
        prev.map(s => 
          s.id === statement.id 
            ? { ...s, opted: statement.opted }
            : s
        )
      )
    } finally {
      // Remove from saving set
      setSavingIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(statement.id)
        return newSet
      })
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <span className="loading loading-spinner loading-md text-white"></span>
      </div>
    )
  }

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-white">Communication Preferences</h2>
      
      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'} mb-4`}>
          <span className="text-white">{message.text}</span>
        </div>
      )}
      
      <div className="prose prose-invert mb-6 max-w-none">
        <p className="text-gray-300">
          Choose how you'd like to hear from us. You can update these preferences at any time.
        </p>
      </div>

      {statements.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          <p>No communication preferences available.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {statements.map((statement) => (
            <div 
              key={statement.id} 
              className="card bg-gray-800 border border-gray-700"
            >
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <h3 className="font-semibold text-lg mb-2 text-white">{statement.text || statement.name}</h3>
                    {statement.description && (
                      <p className="text-gray-400 text-sm">
                        {statement.description}
                      </p>
                    )}
                    {statement.agreedStatement?.dateAgreed && (
                      <p className="text-xs text-gray-500 mt-2">
                        Last agreed: {new Date(statement.agreedStatement.dateAgreed).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input 
                        type="checkbox" 
                        className="sr-only peer"
                        checked={statement.opted || false}
                        onChange={() => handleToggle(statement)}
                        disabled={savingIds.has(statement.id)}
                      />
                      <div className={`
                        w-11 h-6 bg-gray-600 peer-focus:outline-none peer-focus:ring-4 
                        peer-focus:ring-blue-800 rounded-full peer 
                        peer-checked:after:translate-x-full 
                        peer-checked:after:border-white after:content-[''] after:absolute 
                        after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 
                        after:border after:rounded-full after:h-5 after:w-5 after:transition-all 
                        peer-checked:bg-blue-600
                        ${savingIds.has(statement.id) ? 'opacity-50 cursor-not-allowed' : ''}
                      `}>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 p-4 bg-gray-800 rounded-lg border border-gray-700">
        <h3 className="font-semibold mb-2 text-white">Data Protection</h3>
        <p className="text-sm text-gray-400">
          We take your privacy seriously. Your data is stored securely and will only be used in accordance with our privacy policy. 
          You can unsubscribe from any communications at any time by updating your preferences above or by clicking the 
          unsubscribe link in any email we send you.
        </p>
      </div>
    </div>
  )
}

export default CommunicationPreferences